// src/app/core/auth/auth.guard.ts
import type {
  CanActivateFn, ActivatedRouteSnapshot,
  RouterStateSnapshot
} from '@angular/router';
import { Router
} from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from './auth.service';

/**
 * AuthGuard — protege todas las rutas que requieren autenticación.
 *
 * Comportamiento:
 * 1. Si no autenticado → redirige a /login con returnUrl preservado
 * 2. Si autenticado y shouldRenewPassword → redirige a /profile/change-password
 * 3. Si autenticado y no shouldRenewPassword → permite acceso
 *
 * returnUrl es validado para prevenir open redirect:
 * - Debe comenzar con '/'
 * - No puede comenzar con '//' (protocol-relative URL)
 * - No puede contener '://' (URL absoluta disfrazada)
 */
export const authGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const auth   = inject(AuthService);
  const router = inject(Router);

  // ── Verificar autenticación ──────────────────────────────────────────────
  if (!auth.isAuthenticated()) {
    const rawReturnUrl = state.url;
    const safeReturnUrl = validateReturnUrl(rawReturnUrl);

    return router.createUrlTree(['/login'], {
      queryParams: { returnUrl: safeReturnUrl },
    });
  }

  // ── Verificar cambio de contraseña pendiente ──────────────────────────────
  // Si el servidor exige cambio de contraseña, bloquear acceso al dashboard
  // hasta que el cambio se complete exitosamente.
  if (
    auth.shouldRenewPassword() &&
    state.url !== '/profile/change-password'
  ) {
    return router.createUrlTree(['/profile/change-password']);
  }

  return true;
};

/**
 * Valida que un returnUrl sea una ruta relativa segura de la aplicación.
 * Previene ataques de open redirect donde un atacante podría redirigir
 * al usuario a un dominio externo malicioso.
 *
 * Ejemplos:
 *   '/dashboard'               → '/dashboard'     ✅ seguro
 *   '/loans/123'               → '/loans/123'     ✅ seguro
 *   '//evil.com'               → '/dashboard'     ❌ protocol-relative
 *   'https://evil.com'         → '/dashboard'     ❌ URL absoluta
 *   'javascript:alert(1)'      → '/dashboard'     ❌ javascript: scheme
 *   ''                         → '/dashboard'     ❌ vacío
 */
function validateReturnUrl(url: string | null | undefined): string {
  if (!url) return '/dashboard';

  const isRelative  = url.startsWith('/');
  const notProtocolRelative = !url.startsWith('//');
  const notAbsolute = !url.includes('://');
  const notScript   = !url.toLowerCase().startsWith('javascript');

  if (isRelative && notProtocolRelative && notAbsolute && notScript) {
    return url;
  }

  return '/dashboard';
}
